// src/lib/theme.ts — light / dark / match my device, per user (prototype §39, Task R).
//
// The choice lives on the user (`metadata.preferences.theme`, saved with PATCH /auth/preferences) and is cached in
// localStorage so a reload paints in the right theme before /auth/me answers. Dark is a `dark` class on <html>;
// index.css re-declares the palette under it. Only the signed-in app applies it — the public site stays light.
import { useEffect } from 'react';
import { api } from './api';
import { useAuth } from '../stores/auth';
import type { AuthUser } from '../types/api';

export type ThemePref = 'light' | 'dark' | 'auto';
export const THEME_PREFS: ThemePref[] = ['light', 'dark', 'auto'];

const CACHE_KEY = 'lotus.theme';
const isPref = (v: unknown): v is ThemePref => v === 'light' || v === 'dark' || v === 'auto';

/** The cached choice from the last visit (light when none or storage is blocked). */
export function cachedTheme(): ThemePref {
  try { const v = localStorage.getItem(CACHE_KEY); return isPref(v) ? v : 'light'; } catch { return 'light'; }
}
function cache(pref: ThemePref) {
  try { localStorage.setItem(CACHE_KEY, pref); } catch { /* private mode */ }
}

/** The user's saved choice, else the cached one. */
export function themeOf(user: AuthUser | null | undefined): ThemePref {
  const v = (user?.metadata?.preferences as { theme?: unknown } | undefined)?.theme;
  return isPref(v) ? v : cachedTheme();
}

const prefersDark = () => {
  try { return window.matchMedia('(prefers-color-scheme: dark)').matches; } catch { return false; }
};

/** Light or dark right now for a preference. */
export const resolveTheme = (pref: ThemePref): 'light' | 'dark' => (pref === 'auto' ? (prefersDark() ? 'dark' : 'light') : pref);

export function applyTheme(pref: ThemePref) {
  const now = resolveTheme(pref);
  document.documentElement.classList.toggle('dark', now === 'dark');
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', now === 'dark' ? '#121110' : '#212020');
}

/** Back to light — leaving the app for the public site or the sign-in screen. */
export function clearTheme() {
  document.documentElement.classList.remove('dark');
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#212020');
}

/** Apply a preference while mounted (following the device when 'auto'), and clear it on unmount. */
export function useThemeSync(pref: ThemePref) {
  useEffect(() => {
    applyTheme(pref);
    cache(pref);
    let mq: MediaQueryList | null = null;
    const onChange = () => applyTheme(pref);
    if (pref === 'auto') {
      try { mq = window.matchMedia('(prefers-color-scheme: dark)'); mq.addEventListener('change', onChange); } catch { mq = null; }
    }
    return () => { mq?.removeEventListener('change', onChange); };
  }, [pref]);
  useEffect(() => () => clearTheme(), []);
}

/** Save the choice on the user and update the signed-in payload so every screen re-renders with it. */
export async function saveTheme(pref: ThemePref): Promise<void> {
  cache(pref);
  applyTheme(pref);
  const { data } = await api.patch<Record<string, unknown>>('/auth/preferences', { theme: pref });
  const user = useAuth.getState().user;
  if (user) {
    const metadata = { ...(user.metadata ?? {}), preferences: { ...((user.metadata?.preferences as Record<string, unknown>) ?? {}), ...(data ?? {}), theme: pref } };
    useAuth.setState({ user: { ...user, metadata } });
  }
}
